import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { friendlyFileName } from '../lib/downloadMedia';
import { useAuth } from '../contexts/AuthContext';
import { Music, Sparkles, FolderOpen, Type, SlidersHorizontal } from 'lucide-react';
import { useFFmpeg } from '../hooks/useFFmpeg';
import { fetchFile } from '@ffmpeg/util';
import { EditorSegment, AudioTrack, MediaItem, ExportSettings, HistoryEntry, generateId, Caption, CaptionsSettings, TimelineInsertMode, defaultClipFields, defaultExportSettings, defaultCaptionsSettings, DEFAULT_STILL_DURATION, DEFAULT_FADE_SECONDS, formatTime } from '../types/editor';
import PreviewPlayer from '../components/editor/PreviewPlayer';
import EditorTimeline from '../components/editor/EditorTimeline';
import MediaPanel from '../components/editor/MediaPanel';
import AudioPanel from '../components/editor/AudioPanel';
import EditorChat, { EditorChatHandle, InsertGeneratedArgs } from '../components/editor/EditorChat';
import EditorChatHistoryRail from '../components/editor/EditorChatHistoryRail';
import type { EditorChatThread, EditorChatTimelineAudio, EditorChatTimelineCaption, EditorChatTimelineClip } from '../lib/editorChatHistory';
import KlingV2VPanel from '../components/editor/KlingV2VPanel';
import V2VRecolorPanel from '../components/editor/V2VRecolorPanel';
import EditorToolbar from '../components/editor/EditorToolbar';
import CaptionsPanel from '../components/editor/CaptionsPanel';
import ClipInspector from '../components/editor/ClipInspector';
import { useEditorShortcuts } from '../components/editor/useEditorShortcuts';
import { atempoFilter, audioWriteName, canvasSize, durationFromMediaUrl, fetchMediaBlob, findSegmentAtTime, insertIndexAtPlayhead, isLikelyImageSrc, posterFromVideoBlob, previewClipBrightness, segmentEffectiveDuration, totalTimelineDuration } from '../lib/editorTimeline';
import { GalleryPickResult } from '../components/GalleryMediaPicker';
import { assetDisplayName, findOrgMediaAssetByUrl, isImageAsset } from '../lib/mediaGallery';

type SideTab = 'media' | 'audio' | 'clip' | 'ai' | 'captions';

/** Recolor chroma+V2V — hidden until the flow is refined. Keep panel/handlers. */
const RECOLOR_ENABLED = false;

const SIDE_WIDTH_LS = 'seu-editor-side-width';
const TIMELINE_HEIGHT_LS = 'seu-editor-timeline-height';
const SIDE_WIDTH_MIN = 280;
const SIDE_WIDTH_MAX = 640;
const SIDE_WIDTH_DEFAULT = 400;
const TIMELINE_HEIGHT_MIN = 120;
const TIMELINE_HEIGHT_MAX = 420;
const TIMELINE_HEIGHT_DEFAULT = 188;

function readStoredPx(key: string, fallback: number, min: number, max: number): number {
  try {
    const n = Number(localStorage.getItem(key));
    if (Number.isFinite(n)) return Math.min(max, Math.max(min, Math.round(n)));
  } catch { /* private mode */ }
  return fallback;
}

export default function VideoEditor() {
  const { user } = useAuth();
  const ffmpeg = useFFmpeg();

  // ── Core state ──
  const [segments, setSegments] = useState<EditorSegment[]>([]);
  const segmentsRef = useRef<EditorSegment[]>([]);
  segmentsRef.current = segments;
  const [audioTracks, setAudioTracks] = useState<AudioTrack[]>([]);
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([]);
  const [activeSegmentId, setActiveSegmentId] = useState<string | null>(null);
  const [activeAudioId, setActiveAudioId] = useState<string | null>(null);
  const [sideTab, setSideTab] = useState<SideTab>('ai');

  // ── Player state ──
  const videoRef = useRef<HTMLVideoElement>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const currentTimeRef = useRef(0);
  currentTimeRef.current = currentTime;
  const [isPlaying, setIsPlaying] = useState(false);

  // ── AI state ──
  const [v2vSegmentId, setV2vSegmentId] = useState<string | null>(null);
  const [recolorSegmentId, setRecolorSegmentId] = useState<string | null>(null);

  // ── Export ──
  const [exportSettings, setExportSettings] = useState<ExportSettings>(defaultExportSettings);
  const [insertMode, setInsertMode] = useState<TimelineInsertMode>('end');
  const [timelineZoom, setTimelineZoom] = useState(1);
  const [sideWidth, setSideWidth] = useState(() =>
    readStoredPx(SIDE_WIDTH_LS, SIDE_WIDTH_DEFAULT, SIDE_WIDTH_MIN, SIDE_WIDTH_MAX));
  const [timelineHeight, setTimelineHeight] = useState(() =>
    readStoredPx(TIMELINE_HEIGHT_LS, TIMELINE_HEIGHT_DEFAULT, TIMELINE_HEIGHT_MIN, TIMELINE_HEIGHT_MAX));
  const mainRowRef = useRef<HTMLDivElement>(null);
  const editorColRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    try { localStorage.setItem(SIDE_WIDTH_LS, String(sideWidth)); } catch { /* ignore */ }
  }, [sideWidth]);
  useEffect(() => {
    try { localStorage.setItem(TIMELINE_HEIGHT_LS, String(timelineHeight)); } catch { /* ignore */ }
  }, [timelineHeight]);
  const [exporting, setExporting] = useState(false);

  // ── Captions ──
  const [captions, setCaptions] = useState<Caption[]>([]);
  const [captionsSettings, setCaptionsSettings] = useState<CaptionsSettings>(defaultCaptionsSettings);
  const [generatingCaptions, setGeneratingCaptions] = useState(false);

  // ── History (undo/redo) ──
  const emptyHistoryEntry = useCallback((): HistoryEntry => ({
    segments: [],
    audioTracks: [],
    captions: [],
  }), []);
  const [history, setHistory] = useState<HistoryEntry[]>(() => [{
    segments: [],
    audioTracks: [],
    captions: [],
  }]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const historyRef = useRef(history);
  const historyIndexRef = useRef(0);
  historyRef.current = history;
  historyIndexRef.current = historyIndex;
  const chatRef = useRef<EditorChatHandle>(null);
  const pendingChatFrame = useRef<{ b64: string; label: string } | null>(null);
  const [chatHistory, setChatHistory] = useState<EditorChatThread[]>([]);
  const [chatHistoryActiveId, setChatHistoryActiveId] = useState<string | null>(null);
  const handleChatHistoryChange = useCallback((entries: EditorChatThread[], activeId: string | null) => {
    setChatHistory(entries);
    setChatHistoryActiveId(activeId);
  }, []);

  // ── Helpers ──
  const getUserOrgId = useCallback(async () => {
    if (!user) return null;
    const { data } = await supabase.from('organization_members').select('org_id').eq('user_id', user.id).single();
    return data?.org_id || null;
  }, [user]);

  const replaceHistory = useCallback((entry: HistoryEntry) => {
    const snapshot: HistoryEntry = {
      segments: [...entry.segments],
      audioTracks: [...entry.audioTracks],
      captions: [...(entry.captions || [])],
    };
    historyRef.current = [snapshot];
    historyIndexRef.current = 0;
    setHistory([snapshot]);
    setHistoryIndex(0);
  }, []);

  const pushHistory = useCallback((segs: EditorSegment[], audio: AudioTrack[], caps: Caption[]) => {
    const snapshot: HistoryEntry = {
      segments: [...segs],
      audioTracks: [...audio],
      captions: [...caps],
    };
    const prev = historyRef.current;
    const idx = historyIndexRef.current;
    const base = prev.length > 0 ? prev : [emptyHistoryEntry()];
    const keepThrough = Math.max(0, Math.min(idx, base.length - 1));
    const next = [...base.slice(0, keepThrough + 1), snapshot].slice(-30);
    const newIndex = next.length - 1;
    historyRef.current = next;
    historyIndexRef.current = newIndex;
    setHistory(next);
    setHistoryIndex(newIndex);
  }, [emptyHistoryEntry]);

  // ── Computed ──
  const totalDuration = totalTimelineDuration(segments);
  const activeSegment = segments.find(s => s.id === activeSegmentId) || null;
  const canUndo = historyIndex > 0;
  const canRedo = historyIndex >= 0 && historyIndex < history.length - 1;

  const currentSegmentData = React.useMemo(() => {
    const hit = findSegmentAtTime(segments, currentTime);
    if (!hit) return null;
    const duration = segmentEffectiveDuration(hit.segment);
    const prev = hit.index > 0 ? segments[hit.index - 1] : null;
    const fadeIn = Boolean(prev && (prev.transitionOut === 'fade' || prev.transitionOut === 'dissolve'));
    const fadeOut = hit.segment.transitionOut === 'fade' || hit.segment.transitionOut === 'dissolve';
    const brightness = previewClipBrightness({
      localTime: currentTime - hit.startTime,
      duration,
      curve: hit.segment.brightnessCurve || 'none',
      curveSeconds: hit.segment.brightnessCurveSeconds,
      fadeIn,
      fadeOut,
      fadeSeconds: DEFAULT_FADE_SECONDS,
    });
    return { segment: hit.segment, localTime: hit.localTime, startTime: hit.startTime, brightness };
  }, [segments, currentTime]);

  // Sync active segment if we click play or it changes
  useEffect(() => {
    if (isPlaying && currentSegmentData?.segment.id && currentSegmentData.segment.id !== activeSegmentId) {
      setActiveSegmentId(currentSegmentData.segment.id);
    }
  }, [isPlaying, currentSegmentData?.segment.id, activeSegmentId]);

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // MEDIA IMPORT
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  const handleImportFiles = useCallback(async (files: FileList) => {
    if (!ffmpeg.loaded) await ffmpeg.load();

    for (const file of Array.from(files)) {
      const isVideo = file.type.startsWith('video/');
      const isImage = file.type.startsWith('image/');
      if (!isVideo && !isImage) continue;

      const blob = new Blob([await file.arrayBuffer()], { type: file.type });
      const blobUrl = URL.createObjectURL(blob);
      let duration = 0;
      let thumbnailUrl = '';

      if (isVideo) {
        duration = await ffmpeg.getVideoDuration(blob);
        thumbnailUrl = await posterFromVideoBlob(blob);
      } else {
        thumbnailUrl = blobUrl;
      }

      const item: MediaItem = {
        id: generateId(), blob, blobUrl, name: file.name,
        type: isVideo ? 'video' : 'image', duration, thumbnailUrl,
        addedAt: Date.now(),
      };
      setMediaItems(prev => [...prev, item]);
    }
  }, [ffmpeg]);

  const handleImportFromGallery = useCallback(async (result: GalleryPickResult) => {
    if (!ffmpeg.loaded) await ffmpeg.load();
    try {
      const resp = await fetch(result.asset.video_url);
      if (!resp.ok) throw new Error('Falha ao baixar mídia da galeria.');
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      const image = isImageAsset(result.asset);
      let duration = 0;
      let thumbnailUrl = image ? blobUrl : '';
      if (!image) {
        duration = await ffmpeg.getVideoDuration(blob);
        thumbnailUrl = isLikelyImageSrc(result.asset.thumbnail_url)
          ? String(result.asset.thumbnail_url)
          : await posterFromVideoBlob(blob);
      }
      const item: MediaItem = {
        id: generateId(),
        blob,
        blobUrl,
        name: assetDisplayName(result.asset),
        type: image ? 'image' : 'video',
        duration,
        thumbnailUrl,
        addedAt: Date.now(),
        sourceUrl: result.asset.video_url,
        mediaAssetId: result.asset.id,
        generationContext: result.asset.generation_context || undefined,
      };
      setMediaItems(prev => [...prev, item]);
    } catch (err: any) {
      alert(err.message || 'Não foi possível anexar da galeria.');
    }
  }, [ffmpeg]);

  const handleRemoveMedia = useCallback((id: string) => {
    setMediaItems(prev => {
      const item = prev.find(m => m.id === id);
      if (item) URL.revokeObjectURL(item.blobUrl);
      return prev.filter(m => m.id !== id);
    });
  }, []);

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // ADD TO TIMELINE
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  const handleAddToTimeline = useCallback((item: MediaItem) => {
    const isVideo = item.type === 'video';
    const duration = isVideo ? item.duration : DEFAULT_STILL_DURATION;
    const seg: EditorSegment = {
      id: generateId(),
      type: isVideo ? 'video' : 'image',
      blob: item.blob, blobUrl: item.blobUrl,
      duration, originalDuration: duration,
      label: item.name, thumbnailUrl: item.thumbnailUrl,
      sourceUrl: item.sourceUrl,
      mediaAssetId: item.mediaAssetId,
      generationContext: item.generationContext,
      ...defaultClipFields(isVideo ? 'video' : 'image'),
    };
    setSegments(prev => {
      const at = insertMode === 'playhead' ? insertIndexAtPlayhead(prev, currentTime) : prev.length;
      const next = [...prev.slice(0, at), seg, ...prev.slice(at)];
      pushHistory(next, audioTracks, captions);
      return next;
    });
    setActiveSegmentId(seg.id);
  }, [audioTracks, captions, pushHistory, insertMode, currentTime]);

  const handleUpdateSegment = useCallback((id: string, updates: Partial<EditorSegment>) => {
    setSegments(prev => {
      const next = prev.map(s => s.id === id ? { ...s, ...updates } : s);
      pushHistory(next, audioTracks, captions);
      return next;
    });
  }, [audioTracks, captions, pushHistory]);

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // AUDIO IMPORT
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  const handleImportAudio = useCallback(async (files: FileList) => {
    for (const file of Array.from(files)) {
      if (!file.type.startsWith('audio/')) continue;
      const blob = new Blob([await file.arrayBuffer()], { type: file.type });
      const blobUrl = URL.createObjectURL(blob);
      const duration = await ffmpeg.getAudioDuration(blob);

      const track: AudioTrack = {
        id: generateId(), blob, blobUrl, name: file.name,
        duration, volume: 0.5, startOffset: 0,
        trimStart: 0, trimEnd: 0, loop: false, muted: false, speed: 1, kind: 'import',
      };
      setAudioTracks(prev => {
        const next = [...prev, track];
        pushHistory(segments, next, captions);
        return next;
      });
    }
  }, [ffmpeg, segments, captions, pushHistory]);

  const handleUpdateAudioTrack = useCallback((id: string, updates: Partial<AudioTrack>) => {
    setAudioTracks(prev => prev.map(t => t.id === id ? { ...t, ...updates } : t));
  }, []);

  const clampMix = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));

  const handleTimelineAudioMix = useCallback((mix: {
    voice_volume?: number;
    clip_volume?: number;
    music_volume?: number;
    sfx_volume?: number;
    music_loop?: boolean;
    music_speed?: number;
    mute_music?: boolean;
    mute_sfx?: boolean;
    mute_voice?: boolean;
    mute_clips?: boolean;
  }) => {
    if (mix.clip_volume != null || mix.mute_clips != null) {
      setSegments((prev) => {
        const next = prev.map((s) => ({
          ...s,
          volume: mix.clip_volume != null ? clampMix(mix.clip_volume, 0, 1) : s.volume,
          muted: mix.mute_clips != null ? mix.mute_clips : s.muted,
        }));
        return next;
      });
    }
    setAudioTracks((prev) => {
      const hasMusic = prev.some((t) => t.kind === 'music');
      const next = prev.map((t) => {
        const kind = t.kind || 'import';
        const treatAsMusic = kind === 'music' || (!hasMusic && kind === 'import');
        let volume = t.volume;
        let muted = t.muted;
        let loop = t.loop;
        let speed = t.speed && t.speed > 0 ? t.speed : 1;
        if (kind === 'voice' && mix.voice_volume != null) volume = clampMix(mix.voice_volume, 0, 1);
        if (treatAsMusic && mix.music_volume != null) volume = clampMix(mix.music_volume, 0, 1);
        if ((kind === 'sfx' || (!treatAsMusic && kind === 'import')) && mix.sfx_volume != null) {
          volume = clampMix(mix.sfx_volume, 0, 1);
        }
        if (kind === 'voice' && mix.mute_voice != null) muted = mix.mute_voice;
        if (treatAsMusic && mix.mute_music != null) muted = mix.mute_music;
        if ((kind === 'sfx' || (!treatAsMusic && kind === 'import')) && mix.mute_sfx != null) {
          muted = mix.mute_sfx;
        }
        if (treatAsMusic && mix.music_loop != null) loop = mix.music_loop;
        if (treatAsMusic && mix.music_speed != null) speed = clampMix(mix.music_speed, 0.5, 2);
        return { ...t, volume, muted, loop, speed };
      });
      return next;
    });
  }, []);

  const handleDeleteAudio = useCallback((id: string) => {
    setAudioTracks(prev => {
      const track = prev.find(t => t.id === id);
      if (track) URL.revokeObjectURL(track.blobUrl);
      const next = prev.filter(t => t.id !== id);
      pushHistory(segments, next, captions);
      return next;
    });
  }, [segments, captions, pushHistory]);

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // SEGMENT OPERATIONS
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  const handleReorder = useCallback((from: number, to: number) => {
    setSegments(prev => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      pushHistory(next, audioTracks, captions);
      return next;
    });
  }, [audioTracks, captions, pushHistory]);

  const handleDeleteSegment = useCallback((id: string) => {
    setSegments(prev => {
      const next = prev.filter(s => s.id !== id);
      pushHistory(next, audioTracks, captions);
      return next;
    });
    if (activeSegmentId === id) setActiveSegmentId(null);
  }, [audioTracks, captions, activeSegmentId, pushHistory]);

  const handleSpeedChange = useCallback((speed: number, segmentId?: string) => {
    const id = segmentId ?? activeSegmentId;
    if (!id) return;
    setSegments(prev => {
      const next = prev.map(s => s.id === id ? { ...s, speed, duration: (s.originalDuration - s.trimStart - s.trimEnd) } : s);
      pushHistory(next, audioTracks, captions);
      return next;
    });
  }, [activeSegmentId, audioTracks, captions, pushHistory]);

  const handleTrimChange = useCallback((id: string, trimStart: number, trimEnd: number) => {
    setSegments(prev => {
      const next = prev.map(s => {
        if (s.id !== id) return s;
        const safeStart = Math.max(0, Math.min(trimStart, s.originalDuration - 0.5));
        const safeEnd = Math.max(0, Math.min(trimEnd, s.originalDuration - safeStart - 0.5));
        const duration = Math.max(0.5, s.originalDuration - safeStart - safeEnd);
        return { ...s, trimStart: safeStart, trimEnd: safeEnd, duration };
      });
      pushHistory(next, audioTracks, captions);
      return next;
    });
  }, [audioTracks, captions, pushHistory]);

  const handleVolumeChange = useCallback((volume: number) => {
    if (!activeSegmentId) return;
    setSegments(prev => prev.map(s => s.id === activeSegmentId ? { ...s, volume, muted: false } : s));
  }, [activeSegmentId]);

  const handleMuteToggle = useCallback(() => {
    if (!activeSegmentId) return;
    setSegments(prev => prev.map(s => s.id === activeSegmentId ? { ...s, muted: !s.muted } : s));
  }, [activeSegmentId]);

  const handleSplit = useCallback(async (segmentId?: string) => {
    const id = segmentId ?? activeSegmentId;
    if (!id) return;
    const segIndex = segments.findIndex(s => s.id === id);
    if (segIndex < 0) return;
    const seg = segments[segIndex];

    // Calculate relative split time
    let accBefore = 0;
    for (let i = 0; i < segIndex; i++) accBefore += segments[i].duration / segments[i].speed;
    const localTime = (currentTime - accBefore) * seg.speed;
    if (localTime <= 0.5 || localTime >= seg.duration - 0.5) return;

    if (seg.type === 'image') {
      // Split image logically without FFmpeg
      const dur1 = localTime;
      const dur2 = seg.duration - localTime;
      const seg1: EditorSegment = { ...seg, id: generateId(), duration: dur1, originalDuration: dur1, label: seg.label + ' (1)' };
      const seg2: EditorSegment = { ...seg, id: generateId(), duration: dur2, originalDuration: dur2, label: seg.label + ' (2)' };
      setSegments(prev => {
        const next = [...prev];
        next.splice(segIndex, 1, seg1, seg2);
        pushHistory(next, audioTracks, captions);
        return next;
      });
      setActiveSegmentId(seg1.id);
      return;
    }

    if (!ffmpeg.loaded) await ffmpeg.load();

    const result = await ffmpeg.splitVideo(seg.blob, seg.trimStart + localTime);
    if (!result) return;

    const [blob1, blob2] = result;
    const url1 = URL.createObjectURL(blob1);
    const url2 = URL.createObjectURL(blob2);
    const dur1 = await ffmpeg.getVideoDuration(blob1);
    const dur2 = await ffmpeg.getVideoDuration(blob2);

    const seg1: EditorSegment = { ...seg, id: generateId(), blob: blob1, blobUrl: url1, duration: dur1, originalDuration: dur1, trimStart: 0, trimEnd: 0, label: seg.label + ' (1)' };
    const seg2: EditorSegment = { ...seg, id: generateId(), blob: blob2, blobUrl: url2, duration: dur2, originalDuration: dur2, trimStart: 0, trimEnd: 0, label: seg.label + ' (2)' };

    setSegments(prev => {
      const next = [...prev];
      next.splice(segIndex, 1, seg1, seg2);
      pushHistory(next, audioTracks, captions);
      return next;
    });
    setActiveSegmentId(seg1.id);
  }, [activeSegmentId, segments, currentTime, ffmpeg, audioTracks, captions, pushHistory]);

  const handleEditWithKling = useCallback((segmentId: string) => {
    const seg = segments.find(s => s.id === segmentId);
    if (!seg) return;

    setActiveSegmentId(segmentId);
    setIsPlaying(false);

    // Seek playhead to the start of this segment
    let acc = 0;
    for (const s of segments) {
      if (s.id === segmentId) break;
      acc += s.duration / s.speed;
    }
    setCurrentTime(acc);

    // Open dedicated V2V flow
    setRecolorSegmentId(null);
    setV2vSegmentId(segmentId);
  }, [segments]);

  const handleRecolorWithKling = useCallback((segmentId: string) => {
    const seg = segments.find(s => s.id === segmentId);
    if (!seg) return;

    setActiveSegmentId(segmentId);
    setIsPlaying(false);

    let acc = 0;
    for (const s of segments) {
      if (s.id === segmentId) break;
      acc += s.duration / s.speed;
    }
    setCurrentTime(acc);

    setV2vSegmentId(null);
    setRecolorSegmentId(segmentId);
  }, [segments]);

  const prepareV2VClipBlob = useCallback(async (seg: EditorSegment): Promise<Blob> => {
    if (!seg.blob?.size && seg.sourceUrl) {
      const remote = await fetchMediaBlob(seg.sourceUrl);
      if (remote) return remote;
    }
    if (!ffmpeg.loaded) await ffmpeg.load();
    const needsTrim = seg.trimStart > 0 || seg.trimEnd > 0 || seg.speed !== 1;
    if (!needsTrim) return seg.blob;
    const trimmed = await ffmpeg.trimVideo(
      seg.blob,
      seg.trimStart,
      seg.originalDuration - seg.trimEnd,
      seg.speed,
    );
    if (!trimmed) throw new Error('Falha ao preparar o trecho do clip para V2V.');
    return trimmed;
  }, [ffmpeg]);

  const uploadV2VBaseVideo = useCallback(async (orgId: string, blob: Blob): Promise<string> => {
    const { isCdnUploadEnabled } = await import('../lib/mediaUrl');
    const { uploadViaCdn } = await import('../lib/uploadMedia');
    if (!isCdnUploadEnabled()) {
      throw new Error('Upload de mídia indisponível: CDN/S3 desligado neste ambiente.');
    }
    const uploaded = await uploadViaCdn({
      organizationId: orgId,
      file: blob,
      contentType: blob.type || 'video/mp4',
      filename: `v2v_base_${Date.now()}.mp4`,
      purpose: 'v2v_base',
    });
    return uploaded.publicUrl;
  }, []);

  const applyV2VBlobToTimeline = useCallback(async (segmentId: string, blob: Blob, label: string) => {
    if (!ffmpeg.loaded) await ffmpeg.load();
    const blobUrl = URL.createObjectURL(blob);
    const measured = await ffmpeg.getVideoDuration(blob);
    const prev = segments.find((s) => s.id === segmentId);
    // WebM from recolor bake often reports Infinity; keep a finite timeline duration.
    const duration =
      Number.isFinite(measured) && measured > 0
        ? measured
        : (prev && Number.isFinite(prev.duration) && prev.duration > 0 ? prev.duration : 5);
    const thumbnailUrl = await posterFromVideoBlob(blob);

    const segLabel = label.slice(0, 48);
    const mediaItem: MediaItem = {
      id: generateId(), blob, blobUrl, name: segLabel,
      type: 'video', duration, thumbnailUrl, addedAt: Date.now(),
    };
    setMediaItems(prevItems => [...prevItems, mediaItem]);

    const replacement: EditorSegment = {
      id: generateId(),
      type: 'ai_generated',
      blob, blobUrl, duration, originalDuration: duration,
      label: segLabel, thumbnailUrl,
      ...defaultClipFields('video'),
    };

    setSegments(prevSegs => {
      const idx = prevSegs.findIndex(s => s.id === segmentId);
      const next = idx >= 0
        ? [...prevSegs.slice(0, idx), replacement, ...prevSegs.slice(idx + 1)]
        : [...prevSegs, replacement];
      pushHistory(next, audioTracks, captions);
      return next;
    });
    setActiveSegmentId(replacement.id);
    setV2vSegmentId(null);
    setRecolorSegmentId(null);
  }, [ffmpeg, audioTracks, captions, pushHistory, segments]);

  const handleV2VVideoReady = useCallback(async (segmentId: string, videoUrl: string, label: string) => {
    try {
      const cacheBustedUrl = videoUrl + (videoUrl.includes('?') ? '&' : '?') + 'cb=' + Date.now();
      const response = await fetch(cacheBustedUrl);
      if (!response.ok) throw new Error(`Falha no download (HTTP ${response.status})`);
      const blob = await response.blob();
      if (!blob || blob.size === 0) throw new Error('O download resultou em um arquivo vazio.');
      await applyV2VBlobToTimeline(segmentId, blob, label);
    } catch (err) {
      console.error('Failed to apply V2V video:', err);
      alert('Erro ao aplicar vídeo V2V: ' + (err as Error).message);
    }
  }, [applyV2VBlobToTimeline]);

  const handleRecolorLocalBlob = useCallback(async (segmentId: string, blob: Blob, label: string) => {
    try {
      await applyV2VBlobToTimeline(segmentId, blob, label);
    } catch (err) {
      console.error('Failed to apply recolor video:', err);
      alert('Erro ao aplicar recolor: ' + (err as Error).message);
      throw err;
    }
  }, [applyV2VBlobToTimeline]);

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // UNDO / REDO
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  const handleUndo = useCallback(() => {
    if (historyIndexRef.current <= 0) return;
    const nextIndex = historyIndexRef.current - 1;
    const prev = historyRef.current[nextIndex];
    if (!prev) return;
    setSegments([...prev.segments]);
    setAudioTracks([...prev.audioTracks]);
    setCaptions([...(prev.captions || [])]);
    historyIndexRef.current = nextIndex;
    setHistoryIndex(nextIndex);
  }, []);

  const handleRedo = useCallback(() => {
    if (historyIndexRef.current >= historyRef.current.length - 1) return;
    const nextIndex = historyIndexRef.current + 1;
    const next = historyRef.current[nextIndex];
    if (!next) return;
    setSegments([...next.segments]);
    setAudioTracks([...next.audioTracks]);
    setCaptions([...(next.captions || [])]);
    historyIndexRef.current = nextIndex;
    setHistoryIndex(nextIndex);
  }, []);

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // CAPTIONS GENERATION
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  const handleGenerateCaptions = useCallback(async () => {
    if (segments.length === 0 && audioTracks.length === 0) {
      alert('Adicione mídia ou áudio à timeline primeiro.');
      return;
    }
    
    setGeneratingCaptions(true);
    try {
      if (!ffmpeg.loaded) await ffmpeg.load();

      const processedBlobs: Blob[] = [];
      for (const seg of segments) {
        if (seg.type === 'image') continue;
        if (seg.trimStart > 0 || seg.trimEnd > 0 || seg.speed !== 1) {
          const trimmed = await ffmpeg.trimVideo(seg.blob, seg.trimStart, seg.originalDuration - seg.trimEnd, seg.speed);
          if (trimmed) processedBlobs.push(trimmed);
        } else {
          processedBlobs.push(seg.blob);
        }
      }

      let videoBlob: Blob | null = processedBlobs.length === 1
        ? processedBlobs[0]
        : (processedBlobs.length > 0 ? await ffmpeg.concatVideos(processedBlobs, exportSettings.resolution) : null);

      const f = ffmpeg.ffmpeg;
      if (!f) throw new Error('FFmpeg indisponível');

      let hasAudio = false;
      const inputs = [];
      const filterParts = [];
      const audioInputs = [];

      if (videoBlob) {
        await f.writeFile('temp_video.mp4', await fetchFile(videoBlob));
        inputs.push('-i', 'temp_video.mp4');
        filterParts.push(`[0:a]volume=1.0[va]`);
        audioInputs.push('[va]');
        hasAudio = true; 
      }

      const trackNames: string[] = [];
      const mixableTracks = audioTracks.filter((t) => !t.muted);
      for (let i = 0; i < mixableTracks.length; i++) {
        const track = mixableTracks[i];
        const audioName = audioWriteName(track.blob, `track_${i}`);
        trackNames.push(audioName);
        await f.writeFile(audioName, await fetchFile(track.blob));
        inputs.push('-i', audioName);

        const delay = Math.round(track.startOffset * 1000);
        const tempo = atempoFilter(track.speed || 1);
        const tempoBit = tempo ? `${tempo},` : '';
        const loopFilter = track.loop ? ',aloop=loop=-1:size=2e+09' : '';
        const idx = videoBlob ? i + 1 : i;
        filterParts.push(`[${idx}:a]adelay=${delay}|${delay},${tempoBit}volume=${track.volume.toFixed(2)}${loopFilter}[a${i}]`);
        audioInputs.push(`[a${i}]`);
        hasAudio = true;
      }

      if (!hasAudio) throw new Error('Nenhum áudio detectado na timeline');

      const allInputs = audioInputs.join('');
      filterParts.push(`${allInputs}amix=inputs=${audioInputs.length}:duration=first:dropout_transition=2[aout]`);

      await f.exec([
        ...inputs,
        '-filter_complex', filterParts.join(';'),
        '-map', '[aout]',
        '-c:a', 'libmp3lame',
        '-q:a', '9', 
        '-y', 'export_audio.mp3'
      ]);

      const data = await f.readFile('export_audio.mp3');
      // Buffer to base64
      let binary = '';
      const bytes = new Uint8Array(data as any);
      for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
      }
      const base64Audio = btoa(binary);

      if (videoBlob) await f.deleteFile('temp_video.mp4');
      for (const name of trackNames) await f.deleteFile(name);
      await f.deleteFile('export_audio.mp3');

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Usuário não autenticado.');

      const response = await fetch('https://ysrgbdrymjbejtjkklii.supabase.co/functions/v1/generate-captions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ audio_base64: base64Audio })
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Falha ao gerar legendas');
      }

      const result = await response.json();
      if (result.captions && Array.isArray(result.captions)) {
        setCaptions(result.captions.map((c: any) => ({
          id: generateId(),
          text: c.text,
          startTime: Number(c.start_time ?? c.startTime ?? 0),
          endTime: Number(c.end_time ?? c.endTime ?? 0),
          isImpactPhrase: (() => {
            const val = c.is_impact_phrase ?? c.isImpactPhrase;
            if (val === 'false') return false;
            if (val === 'true') return true;
            return Boolean(val);
          })()
        })));
        setSideTab('captions');
      }

    } catch (err: any) {
      console.error('Error generating captions:', err);
      alert('Erro ao gerar legendas: ' + err.message);
    } finally {
      setGeneratingCaptions(false);
    }
  }, [segments, audioTracks, ffmpeg, exportSettings.resolution]);

  const splitSegmentsAt = useCallback(async (list: EditorSegment[], atSeconds: number): Promise<EditorSegment[]> => {
    const hit = findSegmentAtTime(list, atSeconds);
    if (!hit) return list;
    const { index, segment: seg, localTime } = hit;
    if (localTime <= 0.5 || localTime >= seg.duration - 0.5) return list;

    if (seg.type === 'image') {
      const dur1 = localTime;
      const dur2 = seg.duration - localTime;
      const next = [...list];
      next.splice(index, 1,
        { ...seg, id: generateId(), duration: dur1, originalDuration: dur1, label: `${seg.label} (1)` },
        { ...seg, id: generateId(), duration: dur2, originalDuration: dur2, label: `${seg.label} (2)` },
      );
      return next;
    }

    if (!ffmpeg.loaded) await ffmpeg.load();
    const result = await ffmpeg.splitVideo(seg.blob, seg.trimStart + localTime);
    if (!result) return list;
    const [blob1, blob2] = result;
    const dur1 = await ffmpeg.getVideoDuration(blob1);
    const dur2 = await ffmpeg.getVideoDuration(blob2);
    const next = [...list];
    next.splice(index, 1,
      { ...seg, id: generateId(), blob: blob1, blobUrl: URL.createObjectURL(blob1), duration: dur1, originalDuration: dur1, trimStart: 0, trimEnd: 0, label: `${seg.label} (1)` },
      { ...seg, id: generateId(), blob: blob2, blobUrl: URL.createObjectURL(blob2), duration: dur2, originalDuration: dur2, trimStart: 0, trimEnd: 0, label: `${seg.label} (2)` },
    );
    return next;
  }, [ffmpeg]);

  const handleSplitAtSeconds = useCallback(async (atSeconds: number) => {
    const next = await splitSegmentsAt(segments, atSeconds);
    if (next === segments) return;
    setSegments(next);
    pushHistory(next, audioTracks, captions);
  }, [splitSegmentsAt, segments, audioTracks, captions, pushHistory]);

  const handleInsertGenerated = useCallback(async (args: InsertGeneratedArgs) => {
    try {
      let list = segmentsRef.current;
      if (typeof args.atSeconds === 'number' && typeof args.index !== 'number') {
        const hit = findSegmentAtTime(list, args.atSeconds);
        if (hit && hit.localTime > 0.5 && hit.localTime < hit.segment.duration - 0.5) {
          list = await splitSegmentsAt(list, args.atSeconds);
        }
      }

      const cacheBustedUrl = args.url + (args.url.includes('?') ? '&' : '?') + 'cb=' + Date.now();
      const blob = await fetchMediaBlob(cacheBustedUrl) || await fetchMediaBlob(args.url);
      const blobUrl = blob ? URL.createObjectURL(blob) : args.url;
      const isImage = args.kind === 'image';
      let duration = DEFAULT_STILL_DURATION;
      let thumbnailUrl = isImage ? blobUrl : '';
      if (!isImage) {
        if (blob) {
          duration = await ffmpeg.getVideoDuration(blob);
          thumbnailUrl = await posterFromVideoBlob(blob);
        }
        if (!duration) {
          duration = await durationFromMediaUrl(args.url, false, 8);
        }
      }

      const label = (args.label || 'Gerado').slice(0, 48);
      let generationContext = args.generationContext;
      let mediaAssetId = args.mediaAssetId;
      if (!isImage && (!generationContext || !mediaAssetId)) {
        const orgId = await getUserOrgId();
        if (orgId) {
          const asset = await findOrgMediaAssetByUrl(orgId, args.url);
          generationContext = generationContext || asset?.generation_context || undefined;
          mediaAssetId = mediaAssetId || asset?.id;
        }
        if (!generationContext && args.jobId) {
          const { data: jobRow } = await supabase.from('jobs').select('input, model').eq('id', args.jobId).maybeSingle();
          const input = jobRow?.input as { prompt?: string; extras?: { user_prompt?: string }; params?: { duration_seconds?: number; resolution?: string; resolution_id?: string } } | null;
          const prompt = String(input?.prompt || input?.extras?.user_prompt || '').trim();
          if (prompt) {
            const meta = [jobRow?.model, input?.params?.duration_seconds ? `${input.params.duration_seconds}s` : '', input?.params?.resolution || input?.params?.resolution_id]
              .filter(Boolean)
              .join(' · ');
            generationContext = [meta, prompt].filter(Boolean).join('\n').slice(0, 4000);
          }
        }
      }

      setMediaItems((prev) => [...prev, {
        id: generateId(), blob: blob || new Blob(), blobUrl, name: label,
        type: isImage ? 'image' : 'video', duration, thumbnailUrl, addedAt: Date.now(),
        sourceUrl: args.url,
        mediaAssetId,
        generationContext,
      }]);

      const seg: EditorSegment = {
        id: generateId(),
        type: isImage ? 'image' : 'ai_generated',
        blob: blob || new Blob(),
        blobUrl, duration, originalDuration: duration,
        label, thumbnailUrl,
        sourceUrl: args.url,
        mediaAssetId,
        generationContext,
        ...defaultClipFields(isImage ? 'image' : 'video'),
        // I2V/R2V often arrive with the photo's ratio (auto), not the 16:9 canvas.
        // Cover would crop the generated frame; contain shows the whole shot.
        fit: 'contain',
      };

      let insertAt = insertIndexAtPlayhead(list, currentTimeRef.current);
      if (typeof args.index === 'number') insertAt = Math.max(0, Math.min(args.index, list.length));
      else if (typeof args.atSeconds === 'number') insertAt = insertIndexAtPlayhead(list, args.atSeconds);

      const next = [...list.slice(0, insertAt), seg, ...list.slice(insertAt)];
      segmentsRef.current = next;
      setSegments(next);
      pushHistory(next, audioTracks, captions);
      setActiveSegmentId(seg.id);
    } catch (err) {
      console.error('Failed to insert generated media:', err);
      alert('Erro ao adicionar mídia IA: ' + (err as Error).message);
    }
  }, [splitSegmentsAt, ffmpeg, audioTracks, captions, pushHistory, getUserOrgId]);

  const handleAIVideoGenerated = useCallback(async (videoUrl: string, label: string) => {
    await handleInsertGenerated({ url: videoUrl, label, kind: 'video' });
  }, [handleInsertGenerated]);

  const handleRestoreTimelineClips = useCallback(async (
    clips: EditorChatTimelineClip[],
    audio?: EditorChatTimelineAudio[],
    restoredCaptions?: EditorChatTimelineCaption[],
  ) => {
    const revoke = (prev: EditorSegment[]) => {
      prev.forEach((seg) => {
        if (seg.blobUrl && seg.blobUrl.startsWith('blob:')) URL.revokeObjectURL(seg.blobUrl);
        if (seg.thumbnailUrl && seg.thumbnailUrl !== seg.blobUrl && seg.thumbnailUrl.startsWith('blob:')) {
          URL.revokeObjectURL(seg.thumbnailUrl);
        }
      });
    };
    const revokeAudio = (prev: AudioTrack[]) => {
      prev.forEach((track) => {
        if (track.blobUrl && track.blobUrl.startsWith('blob:')) URL.revokeObjectURL(track.blobUrl);
      });
    };
    const resetPlayerClock = () => {
      setCurrentTime(0);
      setIsPlaying(false);
      setActiveSegmentId(null);
      setActiveAudioId(null);
      setV2vSegmentId(null);
      setRecolorSegmentId(null);
      const vid = videoRef.current;
      if (vid) {
        vid.pause();
        vid.removeAttribute('src');
        vid.load();
      }
    };
    if (!clips.length && !audio?.length) {
      setSegments((prev) => {
        revoke(prev);
        return [];
      });
      setAudioTracks((prev) => {
        revokeAudio(prev);
        return [];
      });
      setCaptions(restoredCaptions || []);
      replaceHistory({
        segments: [],
        audioTracks: [],
        captions: restoredCaptions || [],
      });
      resetPlayerClock();
      return;
    }
    const next: EditorSegment[] = [];
    for (const clip of clips) {
      if (!clip.sourceUrl || !/^https?:\/\//i.test(clip.sourceUrl)) continue;
      try {
        const isImage = /image/i.test(clip.type);
        const blob = await fetchMediaBlob(clip.sourceUrl);
        const blobUrl = blob ? URL.createObjectURL(blob) : clip.sourceUrl;
        const defaults = defaultClipFields(isImage ? 'image' : 'video');
        const fromSpan = (clip.end ?? 0) > (clip.start ?? 0) ? (clip.end as number) - (clip.start as number) : 0;
        const duration = clip.duration
          || fromSpan
          || await durationFromMediaUrl(clip.sourceUrl, isImage, DEFAULT_STILL_DURATION);
        let thumbnailUrl = isImage
          ? (clip.thumbnailUrl || blobUrl)
          : (isLikelyImageSrc(clip.thumbnailUrl) ? clip.thumbnailUrl as string : '');
        if (!isImage && !thumbnailUrl && blob) {
          thumbnailUrl = await posterFromVideoBlob(blob);
        }
        next.push({
          id: clip.id || generateId(),
          type: isImage ? 'image' : 'ai_generated',
          blob: blob || new Blob(),
          blobUrl,
          duration,
          originalDuration: clip.originalDuration || duration,
          label: clip.label || 'Clip',
          thumbnailUrl,
          sourceUrl: clip.sourceUrl,
          mediaAssetId: clip.mediaAssetId,
          generationContext: clip.generationContext,
          ...defaults,
          speed: clip.speed && clip.speed > 0 ? clip.speed : defaults.speed,
          trimStart: clip.trimStart || 0,
          trimEnd: clip.trimEnd || 0,
          volume: typeof clip.volume === 'number' ? clip.volume : defaults.volume,
          muted: Boolean(clip.muted),
          fit: clip.fit || defaults.fit,
          transitionOut: clip.transitionOut || defaults.transitionOut,
          motion: clip.motion || defaults.motion,
          brightnessCurve: clip.brightnessCurve || defaults.brightnessCurve,
          brightnessCurveSeconds: clip.brightnessCurveSeconds || defaults.brightnessCurveSeconds,
        });
      } catch (err) {
        console.warn('Failed to restore timeline clip', clip.sourceUrl, err);
      }
    }
    const nextAudio: AudioTrack[] = [];
    for (const track of audio || []) {
      if (!track.sourceUrl || !/^https?:\/\//i.test(track.sourceUrl)) continue;
      try {
        const blob = await fetchMediaBlob(track.sourceUrl);
        const blobUrl = blob ? URL.createObjectURL(blob) : track.sourceUrl;
        let duration = track.duration;
        if (blob && ffmpeg.loaded) {
          try {
            const measured = await ffmpeg.getAudioDuration(blob);
            if (Number.isFinite(measured) && measured > 0) duration = measured;
          } catch { /* keep persisted duration */ }
        }
        if (!(Number.isFinite(duration) && duration > 0)) duration = 8;
        nextAudio.push({
          id: track.id || generateId(),
          blob: blob || new Blob(),
          blobUrl,
          name: track.name || (track.kind === 'sfx' || track.kind === 'music' ? 'Som' : 'Voz'),
          duration,
          volume: typeof track.volume === 'number' ? track.volume : 0.5,
          startOffset: track.startOffset || 0,
          trimStart: track.trimStart || 0,
          trimEnd: track.trimEnd || 0,
          loop: Boolean(track.loop),
          speed: track.speed && track.speed > 0 ? track.speed : 1,
          muted: Boolean(track.muted),
          kind: track.kind || 'voice',
          sourceUrl: track.sourceUrl,
          generationContext: track.generationContext,
        });
      } catch (err) {
        console.warn('Failed to restore timeline audio', track.sourceUrl, err);
      }
    }
    setSegments((prev) => {
      revoke(prev);
      return next;
    });
    setAudioTracks((prev) => {
      revokeAudio(prev);
      return nextAudio;
    });
    const nextCaptions = restoredCaptions || [];
    setCaptions(nextCaptions);
    replaceHistory({
      segments: next,
      audioTracks: nextAudio,
      captions: nextCaptions,
    });
    resetPlayerClock();
    setActiveSegmentId(next[0]?.id || null);
  }, [ffmpeg, replaceHistory]);

  const timelineClips = useMemo(() => {
    let acc = 0;
    return segments.map((s) => {
      const start = acc;
      acc += segmentEffectiveDuration(s);
      return {
        id: s.id,
        label: s.label,
        type: s.type,
        start,
        end: acc,
        sourceUrl: s.sourceUrl,
        blobUrl: s.blobUrl,
        thumbnailUrl: s.thumbnailUrl,
        mediaAssetId: s.mediaAssetId,
        generationContext: s.generationContext,
        duration: s.duration,
        originalDuration: s.originalDuration,
        speed: s.speed,
        trimStart: s.trimStart,
        trimEnd: s.trimEnd,
        volume: s.volume,
        muted: s.muted,
        fit: s.fit,
        transitionOut: s.transitionOut,
        brightnessCurve: s.brightnessCurve,
        brightnessCurveSeconds: s.brightnessCurveSeconds,
        motion: s.motion,
      };
    });
  }, [segments]);

  const timelineContext = useMemo(() => ({
    totalDuration: timelineClips.reduce((sum, clip) => Math.max(sum, clip.end), 0),
    playhead: currentTime,
    selectedId: activeSegmentId,
    clips: timelineClips,
    audioTracks: audioTracks
      .filter((t) => t.sourceUrl && /^https?:\/\//i.test(t.sourceUrl))
      .map((t) => ({
        id: t.id,
        name: t.name,
        sourceUrl: t.sourceUrl as string,
        duration: t.duration,
        startOffset: t.startOffset,
        volume: t.volume,
        muted: t.muted,
        loop: t.loop,
        speed: t.speed,
        kind: t.kind,
        trimStart: t.trimStart,
        trimEnd: t.trimEnd,
        generationContext: t.generationContext,
      })),
    captions: captions.map((c) => ({
      id: c.id,
      text: c.text,
      startTime: c.startTime,
      endTime: c.endTime,
      isImpactPhrase: c.isImpactPhrase,
      position: c.position,
    })),
  }), [timelineClips, currentTime, activeSegmentId, audioTracks, captions]);

  const overlayAudio = useMemo(
    () => audioTracks
      .filter((t) => Boolean(t.blobUrl))
      .map((t) => ({
        id: t.id,
        url: t.blobUrl,
        startOffset: t.startOffset,
        duration: t.duration,
        volume: t.muted ? 0 : t.volume,
        muted: Boolean(t.muted),
        trimStart: t.trimStart,
        trimEnd: t.trimEnd,
        loop: Boolean(t.loop),
        speed: t.speed && t.speed > 0 ? t.speed : 1,
      })),
    [audioTracks],
  );

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // EXPORT
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  const handleExport = useCallback(async () => {
    if (segments.length === 0) return;
    if (!ffmpeg.loaded) await ffmpeg.load();
    setExporting(true);

    try {
      const canvas = canvasSize(exportSettings.aspect, exportSettings.resolution);
      const processedBlobs: Blob[] = [];
      const clipMeta: Array<{
        fit: EditorSegment['fit'];
        fadeIn: boolean;
        fadeOut: boolean;
        duration: number;
        transitionOut: EditorSegment['transitionOut'];
        motion: EditorSegment['motion'];
        brightnessCurve: EditorSegment['brightnessCurve'];
        brightnessCurveSeconds: EditorSegment['brightnessCurveSeconds'];
        volume: number;
        muted: boolean;
      }> = [];

      for (let i = 0; i < segments.length; i++) {
        const seg = segments[i];
        const fadeOut = seg.transitionOut === 'fade';
        const fadeIn = i > 0 && segments[i - 1].transitionOut === 'fade';
        let blob: Blob | null = seg.blob;
        if (seg.type === 'image') {
          blob = await ffmpeg.imageToVideo(
            seg.blob,
            segmentEffectiveDuration(seg),
            canvas.w,
            canvas.h,
            seg.fit || 'cover',
            seg.motion || 'zoom-in',
          );
        } else if (seg.trimStart > 0 || seg.trimEnd > 0 || seg.speed !== 1) {
          blob = await ffmpeg.trimVideo(seg.blob, seg.trimStart, seg.originalDuration - seg.trimEnd, seg.speed);
        }
        if (!blob) continue;
        processedBlobs.push(blob);
        clipMeta.push({
          fit: seg.fit || 'cover',
          fadeIn,
          fadeOut,
          duration: segmentEffectiveDuration(seg),
          transitionOut: seg.transitionOut || 'none',
          motion: seg.type === 'image' ? 'none' : (seg.motion || 'none'),
          brightnessCurve: seg.brightnessCurve || 'none',
          brightnessCurveSeconds: seg.brightnessCurveSeconds,
          volume: Number.isFinite(seg.volume) ? seg.volume : 1,
          muted: Boolean(seg.muted),
        });
      }

      let videoBlob: Blob | null = await ffmpeg.concatVideos(processedBlobs, exportSettings.resolution, {
        aspect: exportSettings.aspect,
        clips: clipMeta,
      });
      if (!videoBlob) throw new Error('Falha na concatenação');

      const mixableTracks = audioTracks.filter((t) => !t.muted);
      const needsFinish = mixableTracks.length > 0 || captions.length > 0 || exportSettings.colorGrade !== 'none';
      if (needsFinish) {
        videoBlob = await ffmpeg.exportFinal(
          videoBlob,
          mixableTracks.map(t => ({ blob: t.blob, volume: t.volume, offset: t.startOffset, loop: t.loop, speed: t.speed })),
          exportSettings.resolution,
          {
            captions,
            settings: captionsSettings,
            colorGrade: exportSettings.colorGrade,
            durationSec: totalTimelineDuration(segments),
          }
        );
        if (!videoBlob) throw new Error('Falha no mix de áudio ou texto');
      }

      // 4. Download
      const url = URL.createObjectURL(videoBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = friendlyFileName({ reference: segments[0]?.label || 'editor', url: 'export.mp4' });
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Export error:', err);
    } finally {
      setExporting(false);
    }
  }, [segments, audioTracks, exportSettings, captions, captionsSettings, ffmpeg]);

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // PLAYER CALLBACKS
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  const handleTimeUpdate = useCallback((t: number) => setCurrentTime(t), []);
  const handlePlayPause = useCallback(() => setIsPlaying(p => !p), []);
  const handleSeek = useCallback((t: number) => setCurrentTime(t), []);

  const flushPendingChatFrame = useCallback(() => {
    const pending = pendingChatFrame.current;
    const attach = chatRef.current?.attachFrame;
    if (!pending || typeof attach !== 'function') return;
    pendingChatFrame.current = null;
    void attach(pending.b64, pending.label);
  }, []);

  const handleCaptureFrame = useCallback((b64: string) => {
    setSideTab('ai');
    const label = `Frame ${formatTime(currentTime)}`;
    const attach = chatRef.current?.attachFrame;
    if (typeof attach === 'function') {
      void attach(b64, label);
      return;
    }
    pendingChatFrame.current = { b64, label };
  }, [currentTime]);

  useEffect(() => {
    flushPendingChatFrame();
  }, [sideTab, flushPendingChatFrame]);

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // RENDER
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  const tabItems: { key: SideTab; icon: React.ReactNode; label: string }[] = [
    { key: 'ai', icon: <Sparkles size={13}/>, label: 'IA' },
    { key: 'media', icon: <FolderOpen size={13}/>, label: 'Mídia' },
    { key: 'clip', icon: <SlidersHorizontal size={13}/>, label: 'Clip' },
    { key: 'audio', icon: <Music size={13}/>, label: 'Áudio' },
    { key: 'captions', icon: <Type size={13}/>, label: 'Texto' },
  ];

  useEditorShortcuts({
    canUndo, canRedo,
    hasActive: Boolean(activeSegmentId),
    currentTime, totalDuration,
    onPlayPause: handlePlayPause,
    onUndo: handleUndo,
    onRedo: handleRedo,
    onSplit: () => { void handleSplit(); },
    onDelete: () => { if (activeSegmentId) handleDeleteSegment(activeSegmentId); },
    onSeek: handleSeek,
    onMute: handleMuteToggle,
  });

  const v2vSegment = v2vSegmentId ? segments.find(s => s.id === v2vSegmentId) ?? null : null;
  const recolorSegment = recolorSegmentId ? segments.find(s => s.id === recolorSegmentId) ?? null : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'stretch', maxWidth: '100%', height: '100%', minHeight: 0 }}>
    <div ref={editorColRef} style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, height: '100%', gap: 8, padding: '8px 12px', overflow: 'hidden' }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .right-panel-scroll::-webkit-scrollbar { width: 4px; }
        .right-panel-scroll::-webkit-scrollbar-track { background: transparent; }
        .right-panel-scroll::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 10px; }
        .editor-timeline-lanes::-webkit-scrollbar { width: 6px; height: 6px; }
        .editor-timeline-lanes::-webkit-scrollbar-track { background: transparent; }
        .editor-timeline-lanes::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.16); border-radius: 10px; }
      `}</style>

      <EditorToolbar
        activeSegment={activeSegment}
        canUndo={canUndo} canRedo={canRedo}
        exporting={exporting} exportProgress={ffmpeg.progress}
        exportSettings={exportSettings}
        onUndo={handleUndo} onRedo={handleRedo}
        onSplit={() => handleSplit()} onDelete={() => activeSegmentId && handleDeleteSegment(activeSegmentId)}
        insertMode={insertMode}
        onInsertModeChange={setInsertMode}
        onSpeedChange={(speed) => handleSpeedChange(speed)} onVolumeChange={handleVolumeChange}
        onMuteToggle={handleMuteToggle}
        onExport={handleExport}
        onExportSettingsChange={(u) => setExportSettings(prev => ({ ...prev, ...u }))}
      />

      {/* Main area: Player + Side panel */}
      <div ref={mainRowRef} style={{ flex: 1, display: 'flex', minHeight: 0, minWidth: 0 }}>
        {/* Player */}
        <div style={{ flex: 1, minWidth: 0, minHeight: 0 }}>
        <PreviewPlayer
          blobUrl={currentSegmentData?.segment.blobUrl || ''}
          mediaKind={currentSegmentData?.segment.type === 'image' ? 'image' : 'video'}
          canvasAspect={exportSettings.aspect}
          fit={currentSegmentData?.segment.fit || 'cover'}
          motion={currentSegmentData?.segment.motion || 'none'}
          brightness={currentSegmentData?.brightness ?? 1}
          segmentDuration={currentSegmentData ? segmentEffectiveDuration(currentSegmentData.segment) : 0}
          segmentStartTime={currentSegmentData?.startTime || 0}
          segmentSpeed={currentSegmentData?.segment.speed || 1}
          segmentVolume={currentSegmentData?.segment.volume ?? 1}
          segmentMuted={currentSegmentData?.segment.muted ?? false}
          currentTime={currentTime}
          isPlaying={isPlaying}
          totalDuration={totalDuration}
          onTimeUpdate={handleTimeUpdate}
          onPlayPause={handlePlayPause}
          onSeek={handleSeek}
          onCaptureFrame={handleCaptureFrame}
          videoRef={videoRef}
          captions={captions}
          captionsSettings={captionsSettings}
          overlayAudio={overlayAudio}
        />
        </div>

        <EditorResizeHandle
          orientation="vertical"
          label="Redimensionar painel"
          onDrag={(clientX) => {
            const row = mainRowRef.current;
            if (!row) return;
            const rect = row.getBoundingClientRect();
            const max = Math.min(SIDE_WIDTH_MAX, Math.max(SIDE_WIDTH_MIN, rect.width - 280));
            const next = Math.round(rect.right - clientX);
            setSideWidth(Math.min(max, Math.max(SIDE_WIDTH_MIN, next)));
          }}
        />

        {/* Side panel */}
        <div style={{
          width: sideWidth,
          flexShrink: 0,
          background: 'rgba(10,10,14,0.95)', borderRadius: 12,
          border: '1px solid rgba(255,255,255,0.06)',
          display: 'flex', flexDirection: 'column', minHeight: 0,
          overflow: 'hidden',
        }}>
          {/* Tabs */}
          <div style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.06)', flexShrink: 0 }}>
            {tabItems.map(tab => (
              <button key={tab.key} onClick={() => setSideTab(tab.key)}
                style={{
                  flex: 1, padding: '8px 4px', border: 'none',
                  background: sideTab === tab.key ? 'rgba(99,102,241,0.1)' : 'transparent',
                  color: sideTab === tab.key ? '#818cf8' : 'rgba(255,255,255,0.35)',
                  borderBottom: sideTab === tab.key ? '2px solid #6366f1' : '2px solid transparent',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  gap: 4, fontSize: '0.65rem', fontWeight: 600, fontFamily: 'inherit',
                  transition: 'all 0.15s',
                }}>
                {tab.icon} {tab.label}
              </button>
            ))}
          </div>

          {/* Panel content */}
          <div
            style={{
              flex: 1, padding: 10, minHeight: 0,
              overflowY: sideTab === 'ai' ? 'hidden' : 'auto',
              display: sideTab === 'ai' ? 'flex' : undefined,
              flexDirection: sideTab === 'ai' ? 'column' : undefined,
              gap: sideTab === 'ai' ? 10 : undefined,
            }}
            className="right-panel-scroll"
          >
            <div style={{
              display: sideTab === 'ai' ? 'flex' : 'none',
              flex: 1,
              minHeight: 0,
              flexDirection: 'column',
            }}>
                <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                  <EditorChat
                    ref={chatRef}
                    variant="panel"
                    selectedSegment={activeSegment}
                    canvasAspect={exportSettings.aspect}
                    getUserOrgId={getUserOrgId}
                    prepareClipBlob={prepareV2VClipBlob}
                    uploadBaseVideo={uploadV2VBaseVideo}
                    getAudioDuration={(blob) => ffmpeg.getAudioDuration(blob)}
                    onVideoGenerated={handleAIVideoGenerated}
                    onInsertGenerated={handleInsertGenerated}
                    onSplitAt={handleSplitAtSeconds}
                    onRestoreTimelineClips={handleRestoreTimelineClips}
                    onSetClipContext={(clipId, context) => {
                      setSegments((prev) => prev.map((s) => (
                        s.id === clipId && !s.generationContext
                          ? { ...s, generationContext: context }
                          : s
                      )));
                    }}
                    onHistoryChange={handleChatHistoryChange}
                    onAddCaption={({ text, startSeconds, endSeconds, position, isImpact }) => {
                      setCaptions((prev) => [...prev, {
                        id: generateId(),
                        text,
                        startTime: startSeconds,
                        endTime: endSeconds,
                        isImpactPhrase: Boolean(isImpact),
                        position,
                      }]);
                    }}
                    onGenerateCaptions={handleGenerateCaptions}
                    onUpdateTimelineAudio={handleTimelineAudioMix}
                    onAddAudioTrack={async ({ url, name, startOffset, kind, loop, generationContext }) => {
                      if (!url || !/^https?:\/\//i.test(url)) {
                        throw new Error('URL de áudio inválida. Use o modal de voz PoYo para gerar locução.');
                      }
                      const res = await fetch(url);
                      if (!res.ok) throw new Error('Falha ao baixar o áudio');
                      const blob = await res.blob();
                      const blobUrl = URL.createObjectURL(blob);
                      const measured = await ffmpeg.getAudioDuration(blob);
                      const duration = Number.isFinite(measured) && measured > 0 ? measured : 8;
                      const hasBed = audioTracks.some((t) => (
                        (t.kind === 'sfx' || t.kind === 'import' || t.kind === 'voice') && !t.muted
                      ));
                      const volume = kind === 'music' && hasBed ? 0.5 : 1;
                      setAudioTracks((prev) => [...prev, {
                        id: generateId(), blob, blobUrl,
                        name: name || (kind === 'sfx' || kind === 'music' ? 'Som' : 'Voz'),
                        duration, volume,
                        startOffset: typeof startOffset === 'number' && Number.isFinite(startOffset)
                          ? startOffset
                          : currentTimeRef.current,
                        trimStart: 0, trimEnd: 0, loop: Boolean(loop),
                        speed: 1,
                        muted: false,
                        kind: kind || 'voice',
                        sourceUrl: url,
                        generationContext,
                      }]);
                      return duration;
                    }}
                    timelineContext={timelineContext}
                    getSegment={(id) => segments.find((s) => s.id === id) || null}
                  />
                </div>
            </div>
            {sideTab === 'media' && (
              <MediaPanel
                mediaItems={mediaItems}
                onImportFiles={handleImportFiles}
                onImportFromGallery={handleImportFromGallery}
                onAddToTimeline={handleAddToTimeline}
                onRemoveMedia={handleRemoveMedia}
              />
            )}
            {sideTab === 'clip' && (
              <ClipInspector
                segment={activeSegment}
                onUpdate={handleUpdateSegment}
                onSpeedChange={(speed) => handleSpeedChange(speed)}
              />
            )}
            {sideTab === 'audio' && (
              <AudioPanel
                audioTracks={audioTracks}
                onImportAudio={handleImportAudio}
                onUpdateTrack={handleUpdateAudioTrack}
                onDeleteTrack={handleDeleteAudio}
              />
            )}
            {sideTab === 'captions' && (
              <CaptionsPanel
                captions={captions}
                settings={captionsSettings}
                isGenerating={generatingCaptions}
                onUpdateCaption={(id, updates) => setCaptions(prev => prev.map(c => c.id === id ? { ...c, ...updates } : c))}
                onDeleteCaption={(id) => setCaptions(prev => prev.filter(c => c.id !== id))}
                onAddCaption={() => setCaptions(prev => [...prev, { id: generateId(), text: '', startTime: currentTime, endTime: currentTime + 2, isImpactPhrase: false }])}
                onSettingsChange={(u) => setCaptionsSettings(prev => ({ ...prev, ...u }))}
                onAutoGenerate={handleGenerateCaptions}
              />
            )}
          </div>
        </div>
      </div>

      <EditorResizeHandle
        orientation="horizontal"
        label="Redimensionar timeline"
        onDrag={(_x, clientY) => {
          const col = editorColRef.current;
          if (!col) return;
          const rect = col.getBoundingClientRect();
          const max = Math.min(TIMELINE_HEIGHT_MAX, Math.max(TIMELINE_HEIGHT_MIN, rect.height - 220));
          const next = Math.round(rect.bottom - clientY);
          setTimelineHeight(Math.min(max, Math.max(TIMELINE_HEIGHT_MIN, next)));
        }}
      />

      {/* Timeline */}
      <div style={{
        height: timelineHeight,
        flexShrink: 0,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
      }}>
        <EditorTimeline
          segments={segments}
          audioTracks={audioTracks}
          currentTime={currentTime}
          totalDuration={totalDuration}
          activeSegmentId={activeSegmentId}
          activeAudioId={activeAudioId}
          onSeek={handleSeek}
          onSegmentClick={setActiveSegmentId}
          onAudioClick={setActiveAudioId}
          onReorder={handleReorder}
          onDeleteSegment={handleDeleteSegment}
          onDeleteAudio={handleDeleteAudio}
          onUpdateAudio={handleUpdateAudioTrack}
          onUpdateSegment={handleUpdateSegment}
          onSpeedChange={(id, speed) => handleSpeedChange(speed, id)}
          onTrimChange={handleTrimChange}
          onSplit={(id) => handleSplit(id)}
          onEditWithKling={handleEditWithKling}
          onRecolorWithKling={RECOLOR_ENABLED ? handleRecolorWithKling : undefined}
          zoom={timelineZoom}
          onZoomChange={setTimelineZoom}
          captions={captions}
        />
      </div>

      {v2vSegment && (
        <KlingV2VPanel
          segment={v2vSegment}
          onClose={() => setV2vSegmentId(null)}
          onVideoReady={handleV2VVideoReady}
          getUserOrgId={getUserOrgId}
          prepareClipBlob={prepareV2VClipBlob}
          uploadBaseVideo={uploadV2VBaseVideo}
        />
      )}

      {RECOLOR_ENABLED && recolorSegment && (
        <V2VRecolorPanel
          segment={recolorSegment}
          onClose={() => setRecolorSegmentId(null)}
          onVideoReady={handleV2VVideoReady}
          onLocalBlobReady={handleRecolorLocalBlob}
          getUserOrgId={getUserOrgId}
          prepareClipBlob={prepareV2VClipBlob}
          uploadBaseVideo={uploadV2VBaseVideo}
        />
      )}

      {/* FFmpeg loading overlay */}
      {ffmpeg.loading && (
        <div style={{
          position: 'fixed', bottom: 16, left: '50%', transform: 'translateX(-50%)',
          background: 'rgba(10,10,14,0.95)', border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 10, padding: '8px 16px', display: 'flex', alignItems: 'center', gap: 8,
          boxShadow: '0 4px 20px rgba(0,0,0,0.5)', zIndex: 9600,
        }}>
          <div style={{ width: 14, height: 14, border: '2px solid #f59e0b', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
          <span style={{ fontSize: '0.72rem', color: '#fbbf24' }}>Carregando processador de vídeo...</span>
        </div>
      )}
    </div>
    <EditorChatHistoryRail
      entries={chatHistory}
      activeId={chatHistoryActiveId}
      onSelect={(thread) => {
        setSideTab('ai');
        chatRef.current?.restoreChat(thread);
      }}
      onNew={() => {
        setSideTab('ai');
        chatRef.current?.startNewChat();
      }}
    />
    </div>
  );
}

function EditorResizeHandle({
  orientation,
  label,
  onDrag,
}: {
  orientation: 'vertical' | 'horizontal';
  label: string;
  onDrag: (clientX: number, clientY: number) => void;
}) {
  const vertical = orientation === 'vertical';
  return (
    <div
      role="separator"
      aria-label={label}
      aria-orientation={vertical ? 'vertical' : 'horizontal'}
      title={label}
      onPointerDown={(e) => {
        e.preventDefault();
        (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
        document.body.style.cursor = vertical ? 'col-resize' : 'row-resize';
        document.body.style.userSelect = 'none';
      }}
      onPointerMove={(e) => {
        if (!e.currentTarget.hasPointerCapture(e.pointerId)) return;
        onDrag(e.clientX, e.clientY);
      }}
      onPointerUp={(e) => {
        if (e.currentTarget.hasPointerCapture(e.pointerId)) {
          e.currentTarget.releasePointerCapture(e.pointerId);
        }
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }}
      onPointerCancel={() => {
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }}
      style={{
        flexShrink: 0,
        width: vertical ? 8 : '100%',
        height: vertical ? 'auto' : 8,
        margin: vertical ? '0 2px' : '0',
        cursor: vertical ? 'col-resize' : 'row-resize',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        touchAction: 'none',
      }}
    >
      <span style={{
        width: vertical ? 3 : 36,
        height: vertical ? 36 : 3,
        borderRadius: 99,
        background: 'rgba(255,255,255,0.18)',
        pointerEvents: 'none',
      }} />
    </div>
  );
}

